<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { ChartBarSquareIcon, UsersIcon } from '@heroicons/vue/24/outline'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import Avatar from '@/components/common/ui/Avatar.vue'
import AvatarMultiselect from '@/components/common/selects/AvatarMultiselect.vue'
import FamilySemesterTable from '@/pages/commercial/FamilySemesterTable.vue'
import { useSalesFamilyStatsStore } from '@/stores/useSalesFamilyStatsStore'

const store = useSalesFamilyStatsStore()

/** UUID des commerciaux sélectionnés (par défaut : toute l'équipe managée). */
const selectedUuids = ref<string[]>([])

const eur = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})
function fmt(n: number): string {
  return eur.format(n ?? 0)
}

const options = computed(() =>
  store.commercials.map((c) => ({ id: c.uuid, text: c.name, subText: c.email })),
)

const result = computed(() => store.result)
const semesters = computed(() => result.value?.semesters ?? [])
const hasData = computed(() => (result.value?.scope.orderCount ?? 0) > 0)
const details = computed(() => result.value?.commercialDetails ?? [])

// Recharge (débouncée) à chaque changement de filtre commercial.
let debounce: ReturnType<typeof setTimeout> | null = null
watch(
  selectedUuids,
  () => {
    if (debounce) clearTimeout(debounce)
    debounce = setTimeout(() => store.fetchData(selectedUuids.value), 250)
  },
  { deep: true },
)

onMounted(async () => {
  await store.fetchCommercials()
  // Pré-sélection de toute l'équipe → la stat équipe ET le détail par personne
  // sont peuplés d'entrée (le watch déclenche le fetch).
  selectedUuids.value = store.commercials.map((c) => c.uuid)
  if (selectedUuids.value.length === 0) await store.fetchData([])
})
</script>

<template>
  <div class="w-full flex flex-col gap-5 p-6 lg:p-8">
    <Bs2Title title="Stats familles" subtitle="Marge par famille et par semestre — équipe managée" />

    <!-- Filtre commercial (multiselect avatar, cf page KPI) -------------------- -->
    <section class="rounded-2xl border border-base-300 bg-base-100 p-4">
      <div class="flex items-center gap-2 mb-3 text-sm font-semibold text-base-content/70">
        <UsersIcon class="size-5" />
        <span>Commerciaux</span>
        <span v-if="store.isLoadingCommercials" class="loading loading-spinner loading-xs" />
      </div>
      <div class="max-w-md">
        <AvatarMultiselect
          v-model="selectedUuids"
          :options="options"
          unit="commercial"
          unit-plural="commerciaux"
        />
      </div>
      <p v-if="!store.isLoadingCommercials && store.commercials.length === 0" class="text-sm text-base-content/50 mt-2">
        Aucun commercial managé.
      </p>
    </section>

    <!-- États ------------------------------------------------------------------ -->
    <div v-if="store.error" class="alert alert-error">
      <span>{{ store.error }}</span>
    </div>

    <div v-if="store.isLoading" class="flex justify-center py-16">
      <span class="loading loading-spinner loading-lg text-primary" />
    </div>

    <template v-else-if="result">
      <div v-if="!hasData" class="rounded-2xl border border-dashed border-base-300 p-10 text-center text-base-content/60">
        Aucune commande validée sur ce périmètre.
      </div>

      <template v-else>
        <!-- Stat équipe : marge par famille × semestre ------------------------ -->
        <section class="rounded-2xl border border-base-300 bg-base-100 overflow-hidden">
          <header class="flex items-center gap-2 px-4 py-3 border-b border-base-300">
            <ChartBarSquareIcon class="size-5 text-primary" />
            <h2 class="font-semibold">Marge par famille — équipe sélectionnée</h2>
            <span class="text-xs text-base-content/50 ml-auto">
              {{ details.length }} commercial(aux) · {{ result.scope.orderCount }} commande(s)
            </span>
          </header>
          <FamilySemesterTable :semesters="semesters" :rows="result.familyRows" :total-row="result.totalRow" />
        </section>

        <!-- Détail par commercial -------------------------------------------- -->
        <section class="flex flex-col gap-3">
          <h2 class="font-semibold text-base-content/80 px-1">Détail par commercial</h2>

          <div
            v-for="c in details"
            :key="c.uuid"
            class="collapse collapse-arrow rounded-2xl border border-base-300 bg-base-100"
          >
            <!-- Ouvert par défaut quand il y a de la marge ; replié si 0 (bruit). -->
            <input type="checkbox" :checked="c.total > 0" />
            <div class="collapse-title flex items-center gap-3 pr-12">
              <div class="avatar">
                <div class="w-8 h-8 rounded-full overflow-hidden">
                  <Avatar :email="c.email" size="48" />
                </div>
              </div>
              <span class="font-semibold">{{ c.name }}</span>
              <span
                class="ml-auto text-sm font-semibold tabular-nums"
                :class="c.total > 0 ? 'text-primary' : 'text-base-content/40'"
              >{{ fmt(c.total) }}</span>
            </div>
            <div class="collapse-content">
              <FamilySemesterTable
                :semesters="semesters"
                :rows="c.familyRows"
                :total-row="c.totalRow"
                total-label="Total commercial"
              />
            </div>
          </div>
        </section>
      </template>
    </template>
  </div>
</template>
